import type { SiteConditions } from '../../domain/foundation/siteConditions';
import { DEFAULT_SITE_CONDITIONS, PLINTH_LENGTH_OPTIONS } from '../../domain/foundation/siteConditions';
import { useT } from '../../i18n';

interface SiteConditionsEditorProps {
    siteConditions: SiteConditions;
    onSiteConditionsChange: (conditions: SiteConditions) => void;
}

const MIN_BURIED_DEPTH_MM = 50;
const MAX_BURIED_DEPTH_MM = 300;
const MIN_SLOPE_PERCENT = -20;
const MAX_SLOPE_PERCENT = 20;

export function SiteConditionsEditor({
    siteConditions,
    onSiteConditionsChange,
}: SiteConditionsEditorProps) {
    const t = useT();
    const updateSiteConditions = <K extends keyof SiteConditions>(key: K, value: SiteConditions[K]) => {
        onSiteConditionsChange({ ...siteConditions, [key]: value });
    };

    const resetSiteConditions = () => {
        onSiteConditionsChange(DEFAULT_SITE_CONDITIONS);
    };

    return (
        <div className="inspector-section-body">
            <div className="inspector-section-toolbar">
                <span className="section-label">{t.site_sectionLabel}</span>
                <button type="button" className="section-action" onClick={resetSiteConditions}>{t.site_reset}</button>
            </div>

            <div className="builder-controls site-conditions-editor">
                <div className="input-row">
                    <label htmlFor="plinth-length">{t.site_plinthLengthLabel}</label>
                    <select
                        id="plinth-length"
                        value={siteConditions.plinthLengthMm}
                        onChange={(event) =>
                            updateSiteConditions('plinthLengthMm', Number(event.target.value) as SiteConditions['plinthLengthMm'])
                        }
                    >
                        {PLINTH_LENGTH_OPTIONS.map((lengthMm) => (
                            <option key={lengthMm} value={lengthMm}>
                                {lengthMm} mm
                            </option>
                        ))}
                    </select>
                </div>

                <div className="slider-row">
                    <div className="slider-label-row">
                        <span className="slider-label">{t.site_minBuriedLabel}</span>
                        <span className="slider-value">{siteConditions.minBuriedDepthMm} mm</span>
                    </div>
                    <input
                        type="range"
                        className="slider-track"
                        aria-label={t.site_minBuriedAriaLabel.replace('{value}', String(siteConditions.minBuriedDepthMm))}
                        min={MIN_BURIED_DEPTH_MM}
                        max={MAX_BURIED_DEPTH_MM}
                        step={10}
                        value={siteConditions.minBuriedDepthMm}
                        onChange={(event) => updateSiteConditions('minBuriedDepthMm', Number(event.target.value))}
                    />
                    <div className="slider-range-labels">
                        <span>{MIN_BURIED_DEPTH_MM} mm</span>
                        <span>{MAX_BURIED_DEPTH_MM} mm</span>
                    </div>
                </div>

                <div className="input-row">
                    <label htmlFor="terrain-mode">{t.site_terrainLabel}</label>
                    <select
                        id="terrain-mode"
                        value={siteConditions.terrainMode}
                        onChange={(event) => updateSiteConditions('terrainMode', event.target.value as SiteConditions['terrainMode'])}
                    >
                        <option value="flat">{t.site_terrainFlat}</option>
                        <option value="slope">{t.site_terrainSlope}</option>
                    </select>
                </div>

                {siteConditions.terrainMode === 'slope' && (
                    <>
                        <div className="slider-row">
                            <div className="slider-label-row">
                                <span className="slider-label">{t.site_slopeAcrossLabel}</span>
                                <span className="slider-value">{siteConditions.slopeAcrossPercent.toFixed(1)}%</span>
                            </div>
                            <input
                                type="range"
                                className="slider-track"
                                aria-label={t.site_slopeAcrossAriaLabel.replace('{value}', siteConditions.slopeAcrossPercent.toFixed(1))}
                                min={MIN_SLOPE_PERCENT}
                                max={MAX_SLOPE_PERCENT}
                                step={0.5}
                                value={siteConditions.slopeAcrossPercent}
                                onChange={(event) => updateSiteConditions('slopeAcrossPercent', Number(event.target.value))}
                            />
                            <div className="slider-range-labels">
                                <span>{MIN_SLOPE_PERCENT}%</span>
                                <span>{MAX_SLOPE_PERCENT}%</span>
                            </div>
                        </div>

                        <div className="slider-row">
                            <div className="slider-label-row">
                                <span className="slider-label">{t.site_slopeAlongLabel}</span>
                                <span className="slider-value">{siteConditions.slopeAlongPercent.toFixed(1)}%</span>
                            </div>
                            <input
                                type="range"
                                className="slider-track"
                                aria-label={t.site_slopeAlongAriaLabel.replace('{value}', siteConditions.slopeAlongPercent.toFixed(1))}
                                min={MIN_SLOPE_PERCENT}
                                max={MAX_SLOPE_PERCENT}
                                step={0.5}
                                value={siteConditions.slopeAlongPercent}
                                onChange={(event) => updateSiteConditions('slopeAlongPercent', Number(event.target.value))}
                            />
                            <div className="slider-range-labels">
                                <span>{MIN_SLOPE_PERCENT}%</span>
                                <span>{MAX_SLOPE_PERCENT}%</span>
                            </div>
                        </div>
                    </>
                )}

                <p className="site-conditions-note">
                    {t.site_note}
                </p>
            </div>
        </div>
    );
}
